import { useTheme } from '@/hooks/useTheme';
import { alarmPalette } from '@/tokens';
import { Header } from '@/components/Header';
import { Scorecard } from '@/components/Scorecard';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/Card';
import { Kicker } from '@/components/Kicker';
import { FilterBar } from '@/components/FilterBar';
import { Pagination } from '@/components/Pagination';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { CrashNotice } from '@/edge-states/CrashNotice';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { LoadingSkeleton } from '@/edge-states/LoadingSkeleton';
import { ErrorState } from '@/edge-states/ErrorState';
import { EmptyState } from '@/edge-states/EmptyState';
import { FilterEmpty } from '@/edge-states/FilterEmpty';
import { fmtDate, relDate } from '@/lib/date-utils';
import { pickHeadline } from '@/lib/pick-headline';
import type { DisplayItem, DisplayStats } from '@/types';
import type { TabFilterConfig } from '@/lib/filters';
import { track, toAnalyticsItemType, type FeedPosition } from '@/lib/analytics';

interface HomeProps {
  items: DisplayItem[];
  stats: DisplayStats;
  loading: boolean;
  error: boolean;
  onOpenItem: (id: string | number) => void;
  filterConfig?: TabFilterConfig;
  activeFilters?: Record<string, string>;
  onFilterChange?: (key: string, value: string | null) => void;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  total?: number;
  filteredTotal?: number;
}

export function Home({
  items, stats, loading, error, onOpenItem,
  filterConfig, activeFilters, onFilterChange, onClearFilters,
  hasActiveFilters, searchQuery, onSearchChange,
  currentPage, totalPages, onPageChange,
  total, filteredTotal,
}: HomeProps) {
  const { theme, headType, mode } = useTheme();
  const headlineMode = 'spicy';
  const showFiltered = hasActiveFilters || false;
  // When the Tracker owns "/", the story feed is the News tab
  const trackerHome = useFeatureFlag('rap_sheet');

  // card_open lives here, not in Card: Card doesn't know its position or tab,
  // and the hero <h1> + featured card open items without going through Card.
  // Card's interface is unchanged; it just receives a closure per slot.
  const tab = filterConfig?.tabType ?? 'stories';
  const openWithTracking = (item: DisplayItem, position: FeedPosition) => {
    const item_type = toAnalyticsItemType(item.type);
    if (item_type) track('card_open', { item_type, alarm_level: item.alarm, feed_position: position, tab });
    onOpenItem(item.id);
  };

  const navLabel = filterConfig?.tabType === 'eos' ? 'Executive Orders'
    : filterConfig?.tabType === 'scotus' ? 'Supreme Court'
    : filterConfig?.tabType === 'pardons' ? 'Pardons'
    : trackerHome ? 'News'
    : 'Home';

  const filterBar = filterConfig && activeFilters && onFilterChange && onClearFilters ? (
    <FilterBar
      config={filterConfig}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      onClearAll={onClearFilters}
      hasActiveFilters={hasActiveFilters || false}
      total={total ?? items.length}
      filteredTotal={filteredTotal ?? items.length}
    />
  ) : null;

  const listKey = `${currentPage}|${JSON.stringify(activeFilters ?? {})}|${loading}|${error}`;
  const shell = (children: React.ReactNode) => (
    <div style={{ background: theme.bg, color: theme.ink, fontFamily: headType.sans, minHeight: '100vh' }}>
      <Header
        current={navLabel}
        searchPlaceholder={filterConfig?.searchPlaceholder}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
      />
      <Scorecard stats={stats} />
      <main id="main-content" style={{ maxWidth: 1400, margin: '0 auto', padding: '0 20px' }}>
        {/* A card list crash keeps the header and paging; paging, filtering
            or the next load tries again (ADO-196) */}
        <ErrorBoundary resetKey={listKey} fallback={<CrashNotice />}>
          {children}
        </ErrorBoundary>
        {totalPages != null && totalPages > 1 && onPageChange && (
          <Pagination page={currentPage ?? 1} totalPages={totalPages} onPageChange={(p) => {
            onPageChange(p);
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }} />
        )}
        <Footer />
      </main>
    </div>
  );

  if (loading) return shell(<LoadingSkeleton />);
  if (error) return shell(<ErrorState />);
  if (items.length === 0 && showFiltered) return shell(<FilterEmpty label="these filters" onReset={onClearFilters!} />);
  if (items.length === 0) return shell(<EmptyState />);

  // When filters active: flat grid (no hero/featured)
  if (showFiltered) {
    return shell(
      <>
        {filterBar}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))',
          gap: 20, padding: '24px 0 0',
        }}>
          {items.map((it, i) => (
            <Card key={it.id} item={it} headlineMode={headlineMode} onOpen={() => openWithTracking(it, i)} />
          ))}
        </div>
      </>
    );
  }

  // Default: hero + featured + grid
  const sortedAll = [...items].sort((a, b) => b.alarm - a.alarm || b.updated.localeCompare(a.updated));
  const lead = sortedAll[0];
  const featuredSecond = sortedAll.find(e => e.id !== lead.id && e.alarm >= 4);
  const rest = items
    .filter(e => e.id !== lead.id && (!featuredSecond || e.id !== featuredSecond.id))
    .sort((a, b) => b.published.localeCompare(a.published));
  const cLead = alarmPalette(lead.alarm, 'restrained', mode, 'midnight');

  return shell(
    <>
      {/* HERO */}
      <section aria-label="Lead story" style={{ padding: '44px 0 36px', borderBottom: `1px solid ${theme.line}`, position: 'relative' }}>
        <div style={{ maxWidth: 920 }}>
          <Kicker theme={theme} type={headType} item={lead} accent={cLead.accent} />
          <h1 onClick={() => openWithTracking(lead, 'hero')} style={{
            fontFamily: headType.display, fontWeight: 600,
            fontSize: 'clamp(32px, 4.0vw, 52px)', lineHeight: 1.04,
            letterSpacing: '-0.02em', textWrap: 'balance',
            margin: 0, cursor: 'pointer', color: theme.ink,
          }}>
            {pickHeadline(lead, headlineMode)}
          </h1>
          <p style={{ fontFamily: headType.display, fontSize: 18, lineHeight: 1.5, color: theme.dim, marginTop: 18, maxWidth: 720, textWrap: 'pretty' }}>
            {lead.dek}
          </p>
          <div style={{ fontFamily: headType.mono, fontSize: 11, color: theme.dim, marginTop: 18, display: 'flex', gap: 12, flexWrap: 'wrap', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            <span>Filed {fmtDate(lead.published)}</span>
            <span>·</span>
            <span>Updated {relDate(lead.updated)}</span>
            {lead.sources.length > 1 && (<><span>·</span><span>{lead.sources.length} sources</span></>)}
          </div>
        </div>
      </section>

      {/* FILTERS (below hero, above featured) */}
      {filterBar}

      {/* FEATURED CARD */}
      {featuredSecond && (
        <div style={{ padding: '28px 0 0' }}>
          <Card item={featuredSecond} headlineMode={headlineMode} onOpen={() => openWithTracking(featuredSecond, 'featured')} featured />
        </div>
      )}

      {/* GRID */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: 20, padding: '24px 0 0' }}>
        {rest.map((it, i) => (
          <Card key={it.id} item={it} headlineMode={headlineMode} onOpen={() => openWithTracking(it, i)} />
        ))}
      </div>
    </>
  );
}
